import React, { useState, useEffect, useCallback } from 'react';
import { leaderboardAPI, groupAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ProfilePhoto } from '../ProfilePhoto';
import { Coins, Zap, Trophy, Medal, Crown, UsersRound } from 'lucide-react';
import { toast } from 'sonner';

// Helper functions outside component
const getRankIcon = (rank) => {
  if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-slate-400" />;
  if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
  return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-slate-500">{rank}</span>;
};

const getRankBg = (rank) => {
  if (rank === 1) return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200';
  if (rank === 2) return 'bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200';
  if (rank === 3) return 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200';
  return 'bg-white border-slate-100';
};

// Separate component for list rendering
function LeaderboardList({ data, valueLabel, valueIcon: ValueIcon, valueColor }) {
  return (
    <div className="space-y-2">
      {data.entries?.length === 0 ? (
        <p className="text-center text-slate-500 py-8">No hay datos aún</p>
      ) : (
        data.entries?.map((entry) => (
          <div
            key={entry.user_id}
            className={`flex items-center gap-4 p-3 rounded-xl border transition-all hover:shadow-md ${getRankBg(entry.rank)}`}
            data-testid={`leaderboard-entry-${entry.rank}`}
          >
            <div className="w-8 flex justify-center">
              {getRankIcon(entry.rank)}
            </div>
            <ProfilePhoto photoUrl={entry.photo_url} name={entry.user_name} size={40} />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-slate-900 truncate">{entry.user_name}</p>
              {entry.level && (
                <p className="text-xs text-slate-500">Nivel {entry.level}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <ValueIcon className={`w-5 h-5 ${valueColor}`} />
              <span className="font-bold text-lg">{entry.value.toLocaleString()}</span>
              <span className="text-xs text-slate-500">{valueLabel}</span>
            </div>
          </div>
        ))
      )}
      {data.total_students > 0 && (
        <p className="text-center text-sm text-slate-400 pt-2">
          {data.total_students} estudiantes en total
        </p>
      )}
    </div>
  );
}

// Separate component for group list rendering
function GroupLeaderboardList({ data, valueLabel, valueIcon: ValueIcon, valueColor }) {
  return (
    <div className="space-y-2">
      {data.entries?.length === 0 ? (
        <p className="text-center text-slate-500 py-8">No hay grupos creados</p>
      ) : (
        data.entries?.map((entry) => (
          <div
            key={entry.group_id}
            className={`flex items-center gap-4 p-3 rounded-xl border transition-all hover:shadow-md ${getRankBg(entry.rank)}`}
            data-testid={`group-leaderboard-entry-${entry.rank}`}
          >
            <div className="w-8 flex justify-center">
              {getRankIcon(entry.rank)}
            </div>
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${entry.group_color}20` }}
            >
              <UsersRound className="w-5 h-5" style={{ color: entry.group_color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-slate-900 truncate">{entry.group_name}</p>
              <p className="text-xs text-slate-500">{entry.member_count} miembros</p>
            </div>
            <div className="flex items-center gap-2">
              <ValueIcon className={`w-5 h-5 ${valueColor}`} />
              <span className="font-bold text-lg">{entry.value.toLocaleString()}</span>
              <span className="text-xs text-slate-500">{valueLabel}</span>
            </div>
          </div>
        ))
      )}
      {data.total_groups > 0 && (
        <p className="text-center text-sm text-slate-400 pt-2">
          {data.total_groups} grupos en total
        </p>
      )}
    </div>
  );
}

export function LeaderboardTab({ classId }) {
  const [coinsLeaderboard, setCoinsLeaderboard] = useState({ entries: [], total_students: 0 });
  const [xpLeaderboard, setXpLeaderboard] = useState({ entries: [], total_students: 0 });
  const [levelLeaderboard, setLevelLeaderboard] = useState({ entries: [], total_students: 0 });
  const [groupCoinsLeaderboard, setGroupCoinsLeaderboard] = useState({ entries: [], total_groups: 0 });
  const [groupXpLeaderboard, setGroupXpLeaderboard] = useState({ entries: [], total_groups: 0 });
  const [loading, setLoading] = useState(true);

  const loadLeaderboards = useCallback(async () => {
    setLoading(true);
    try {
      const [coinsRes, xpRes, levelRes, groupCoinsRes, groupXpRes] = await Promise.all([
        leaderboardAPI.getByCoins(classId, 10),
        leaderboardAPI.getByXP(classId, 10),
        leaderboardAPI.getByLevel(classId, 10),
        groupAPI.getLeaderboard(classId, 'coins'),
        groupAPI.getLeaderboard(classId, 'xp')
      ]);
      setCoinsLeaderboard(coinsRes.data);
      setXpLeaderboard(xpRes.data);
      setLevelLeaderboard(levelRes.data);
      setGroupCoinsLeaderboard(groupCoinsRes.data);
      setGroupXpLeaderboard(groupXpRes.data);
    } catch (error) {
      toast.error('Error cargando leaderboards');
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    loadLeaderboards();
  }, [loadLeaderboards]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leaderboard-tab">
      <div className="flex items-center gap-3">
        <Trophy className="w-6 h-6 text-yellow-500" />
        <h2 className="text-xl font-bold">Tabla de Posiciones</h2>
      </div>

      <Tabs defaultValue="coins" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="coins" className="flex items-center gap-2" data-testid="tab-coins">
            <Coins className="w-4 h-4" />
            Coins
          </TabsTrigger>
          <TabsTrigger value="xp" className="flex items-center gap-2" data-testid="tab-xp">
            <Zap className="w-4 h-4" />
            XP
          </TabsTrigger>
          <TabsTrigger value="level" className="flex items-center gap-2" data-testid="tab-level">
            <Trophy className="w-4 h-4" />
            Nivel
          </TabsTrigger>
          <TabsTrigger value="groups" className="flex items-center gap-2" data-testid="leaderboard-tab-groups">
            <UsersRound className="w-4 h-4" />
            Grupos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="coins" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Coins className="w-5 h-5 text-yellow-500" />
                Top por Coins
              </CardTitle>
            </CardHeader>
            <CardContent>
              <LeaderboardList 
                data={coinsLeaderboard} 
                valueLabel="coins" 
                valueIcon={Coins} 
                valueColor="text-yellow-500" 
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="xp" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Zap className="w-5 h-5 text-blue-500" />
                Top por Experiencia
              </CardTitle>
            </CardHeader>
            <CardContent>
              <LeaderboardList 
                data={xpLeaderboard} 
                valueLabel="XP" 
                valueIcon={Zap} 
                valueColor="text-blue-500" 
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="level" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Trophy className="w-5 h-5 text-purple-500" />
                Top por Nivel
              </CardTitle>
            </CardHeader>
            <CardContent>
              <LeaderboardList 
                data={levelLeaderboard} 
                valueLabel="nivel" 
                valueIcon={Trophy} 
                valueColor="text-purple-500" 
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="groups" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Coins className="w-5 h-5 text-yellow-500" />
                  Grupos por Coins
                </CardTitle>
              </CardHeader>
              <CardContent>
                <GroupLeaderboardList 
                  data={groupCoinsLeaderboard} 
                  valueLabel="coins" 
                  valueIcon={Coins} 
                  valueColor="text-yellow-500" 
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Zap className="w-5 h-5 text-blue-500" />
                  Grupos por XP
                </CardTitle>
              </CardHeader>
              <CardContent>
                <GroupLeaderboardList 
                  data={groupXpLeaderboard} 
                  valueLabel="XP" 
                  valueIcon={Zap} 
                  valueColor="text-blue-500" 
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default LeaderboardTab;
